import { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { QUOTE_JOURNEY_TYPE } from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import axioInstance from "@/libs/axios";
import { updateRequiredInformation } from "@/store/slices/quote/quote.slice";
import { RootState } from "@/store/store";
import { QuoteData } from "@/types/quote/quote-data";
import { Traveller } from "@/types/Traveller";
import logError from "@/utils/logger";

interface RequiredInformationPayload {
  organiser: {
    firstLineOfAddress: string;
    secondLineOfAddress?: string;
    city: string;
    postCode: string;
    identificationType: string;
    identificationNumber: string;
  };
  travellers: Array<{
    encryptedId: string;
    identificationType?: string;
    identificationNumber?: string;
  }>;
}

export default function useSaveRequiredInformation(resetTrigger: () => void) {
  const dispatch = useDispatch();
  const [data, setData] = useState<null | QuoteData>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | AxiosError>(null);
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const { attributes, version, referenceId } = quoteResponse;

  const saveRequiredInformation = useCallback(
    async (payload: RequiredInformationPayload) => {
      // First, update Redux state
      dispatch(updateRequiredInformation(payload));

      // Prepare updated attributes with the new information
      const updatedAttributes = {
        ...attributes,
        organiser: {
          ...attributes.organiser,
          ...payload.organiser,
        },
        travellerDetails: {
          ...attributes.travellerDetails,
          traveller: attributes.travellerDetails?.traveller?.map(
            (t: Traveller): Traveller => {
              const travellerUpdate = payload.travellers.find(
                (pt) => pt.encryptedId === t.encryptedId
              );
              if (!travellerUpdate) return t;

              // Only update identification fields if BOTH are present in payload
              // This preserves existing values when fields are not sent
              if (
                travellerUpdate.identificationType !== undefined &&
                travellerUpdate.identificationNumber !== undefined
              ) {
                return {
                  ...t,
                  identificationType: travellerUpdate.identificationType,
                  identificationNumber: travellerUpdate.identificationNumber,
                };
              }

              // If identification fields not in payload, return unchanged
              return t;
            }
          ),
        },
      };

      const body = {
        data: {
          type: QUOTE_JOURNEY_TYPE,
          channel: "ONLINE",
          version,
          viewPage: Routes.RequiredInformation,
          attributes: updatedAttributes,
        },
      };

      try {
        setLoading(true);
        setError(null);
        const response = await axioInstance.patch(
          `${process.env.NEXT_PUBLIC_BASE_URL}/quote/${referenceId}/initialQuestionSet`,
          body
        );
        setData(response.data.data);
      } catch (err) {
        const axiosError = err as AxiosError;
        logError(axiosError, { location: "Required Information Page Save" });
        setError(axiosError);
      } finally {
        setLoading(false);
        resetTrigger();
      }
    },
    [attributes, referenceId, resetTrigger, version, dispatch]
  );

  return { data, loading, error, saveRequiredInformation };
}
